import { useState, useEffect } from 'react';
import { isRunningInApp } from '@/lib/appRedirect';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export function useAppDownloadBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'pwa' | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    console.log('🚀 [AppDownloadBanner] Hook initialized');
    
    // Don't show if we're already in the app
    if (isRunningInApp()) {
      console.log('📱 [AppDownloadBanner] Running in Capacitor app, not showing banner');
      return;
    }

    // Check if user has dismissed the banner recently (24h cooldown)
    const dismissedTime = localStorage.getItem('app-download-banner-dismissed');
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    
    if (dismissedTime && (now - parseInt(dismissedTime)) < twentyFourHours) {
      console.log('⏰ [AppDownloadBanner] Banner dismissed recently, not showing');
      return;
    }

    // Detect platform with robust iOS detection
    const userAgent = navigator.userAgent;
    console.log('🔍 [AppDownloadBanner] User agent:', userAgent);
    
    // More robust iOS detection (works with Chrome on iOS)
    const isIOSDevice = () => {
      return [
        'iPad Simulator',
        'iPhone Simulator', 
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ].includes(navigator.platform) 
      || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
      || /iPad|iPhone|iPod/.test(userAgent);
    };
    
    const isAndroid = /Android/.test(userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    // Check if already installed as PWA
    if (isStandalone) {
      console.log('📲 [AppDownloadBanner] Already installed as PWA');
      return;
    }
    
    console.log('🍎 [AppDownloadBanner] iOS detected:', isIOSDevice());
    console.log('🤖 [AppDownloadBanner] Android detected:', isAndroid);
    
    if (isIOSDevice()) {
      console.log('✅ [AppDownloadBanner] Setting iOS platform and showing banner');
      setPlatform('ios');
      setIsVisible(true);
    } else if (isAndroid) {
      console.log('✅ [AppDownloadBanner] Setting Android platform and showing banner');  
      setPlatform('android');
      setIsVisible(true);
    } else {
      // Fallback for Chrome on iOS (CriOS)
      const looksLikeIOSChrome = /CriOS/.test(userAgent) && /iPhone|iPad|iPod/.test(userAgent);
      if (looksLikeIOSChrome) {
        console.log('✅ [AppDownloadBanner] Fallback matched: iOS Chrome detected');
        setPlatform('ios');
        setIsVisible(true);
      }
    }

    // Listen for PWA install prompt (Chrome/Edge)
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('💫 [AppDownloadBanner] PWA install prompt available');
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setPlatform('pwa');
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const dismissBanner = () => {
    console.log('🗑️ [AppDownloadBanner] Banner dismissed by user');
    setIsVisible(false);
    localStorage.setItem('app-download-banner-dismissed', Date.now().toString());
  };

  const installPWA = async () => {
    if (deferredPrompt) {
      console.log('🎯 [AppDownloadBanner] Triggering PWA install prompt');
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('📊 [AppDownloadBanner] Install outcome:', outcome);
      
      if (outcome === 'accepted') {
        setIsVisible(false);
      }
      setDeferredPrompt(null);
    }
  };

  const openAppStore = () => {
    console.log('🏪 [AppDownloadBanner] Opening app store for platform:', platform);
    if (platform === 'ios') {
      window.open('https://apps.apple.com/app/id6752715950', '_blank');
    }
  };

  console.log('🎯 [AppDownloadBanner] Current state:', { isVisible, platform, hasPWAPrompt: !!deferredPrompt });

  return {
    isVisible,
    platform,
    dismissBanner,
    openAppStore,
    installPWA,
    hasPWAPrompt: !!deferredPrompt
  };
}